import { Prop, Component, h, Host, Method } from "@stencil/core";
import classNames from "classnames";
import { ButtonProps } from "./f-button-model";

/**
 * @name    a-button
 * @type    atom
 *
 * @param   {string} type                 Type of Attribute (button, submit, reset)
 * @param   {string} variant              Type of Button Definition (Primary, Secondary, Tertiary, Integrated or transparent)
 * @param   {boolean} iconOnly            Button only contains icon
 * @param   {boolean} disabled            Disable or not the Button
 * @param   {string} linkUrl              Href attribute when rendered as a link with "a" HTML tag
 * @param   {boolean} fixedWidth          Fixed or not fixed width
 * @param   {Object} additionalProps      Additional properties for the button
 *
 * @description
 * representation of buttons
 */

@Component({
  tag: "f-button",
  styleUrl: "f-button.scss",
  shadow: true,
})
export class FButton {
  private button: HTMLFButtonElement;
  private className = "a-button";

  @Prop() type: ButtonProps["type"] = "button";
  @Prop() variant: ButtonProps["variant"] = "primary";
  @Prop() iconOnly: ButtonProps["iconOnly"];
  @Prop() disabled: ButtonProps["disabled"];
  @Prop() linkUrl: ButtonProps["linkUrl"];
  @Prop() fixedWidth: ButtonProps["fixedWidth"];
  @Prop() fullWidth: ButtonProps["fullWidth"];
  @Prop() notCentered: ButtonProps["notCentered"];
  @Prop() additionalProps: ButtonProps["additionalProps"] = {};
  @Prop() buttonTitle: ButtonProps["buttonTitle"];
  @Prop() ttStaticHeight: ButtonProps["ttStaticHeight"] = false;
  @Prop() ttHeaderSearch: ButtonProps["ttHeaderSearch"] = false; // TT Custom

  @Method()
  async focusOnButton(): Promise<void> {
    window.setTimeout(() => {
      this.button.focus();
    }, 0);
  }

  render() {
    const buttonClass = classNames(this.className, {
      [`${this.className}--${this.variant}`]: this.variant,
      [`${this.className}--fixed`]: this.fixedWidth,
      [`${this.className}--full-width`]: this.fullWidth,
      [`${this.className}--icon-only`]: this.iconOnly,
      [`${this.className}--not-centered`]: this.notCentered,
      [`${this.className}--tt-static-height`]: this.ttStaticHeight,
      [`${this.className}--tt-header-search`]: this.ttHeaderSearch, // TT Custom
    });
    const CustomTag = this.linkUrl ? "a" : "button";
    return (
      <Host class={{ fullWidth: this.fullWidth, disabled: this.disabled }}>
        <CustomTag
          {...(!this.linkUrl && { type: this.type })}
          href={!this.disabled && this.linkUrl}
          class={buttonClass}
          disabled={this.disabled}
          {...(typeof this.additionalProps === "string"
            ? JSON.parse(this.additionalProps)
            : this.additionalProps)}
          ref={(el) => (this.button = el)}
          title={this.buttonTitle}
          data-test="button"
        >
          <span class="a-button__label" data-test="button-label">
            <slot />
          </span>
        </CustomTag>
      </Host>
    );
  }
}
